/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals firebase */

/**
 * Represents the business service handler class.
 */

export default class Service {

    /**
     * Loads the specified user by uid or all of the users.
     * 
     * @param   {String}    uid         uid of the user (Optional)
     * @return  {Promise}   promise     promise object
     */

    static loadUser(uid) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Checking cache

            if (window.users) {

                // Searching user in the cache

                if (uid) {
                    for (let i = 0; i < window.users.length; i++) {
                        if (window.users[i].uid === uid) {
                            resolve(JSON.parse(JSON.stringify(window.users[i])));
                            break;
                        }
                    }
                } else {
                    resolve(JSON.parse(JSON.stringify(window.users)));
                }
            } else {

                // Getting the requested user

                if (uid) {

                    // Loading the user

                    firebase.database().ref(`/users/${uid}`).once('value').then((snapshot) => {

                        // User loaded successfully

                        let result = snapshot.val();
                    
                        result.uid = uid;

                        // Resolving with the user

                        resolve(result);
                    }).catch((error) => {

                        // Error occured during the database reading
    
                        reject(error);
                    });
                } else {

                    // Loading the user(s)

                    firebase.database().ref('/users').on('value', (snapshot) => {

                        // User(s) loaded successfully

                        let result = snapshot.val();

                        // Getting all of the users

                        let users = [];

                        Object.keys(result).forEach((key) => {
                            let item = result[key];
                            item.uid = key;
                            users.push(item);
                        });

                        // Sorting the users by name

                        users.sort((a, b) => {
                            let nameA = a.name.toUpperCase();
                            let nameB = b.name.toUpperCase();

                            if (nameA < nameB) {
                                return -1;
                            } else if (nameA > nameB) {
                                return 1;
                            } else {
                                return 0;
                            }                    
                        });

                        // Resolving with the users

                        window.users = users;
                        resolve(users);
                    });
                }
            }
        });
    }

    /**
     * Creates a new user.
     * 
     * @param   {String}    uid         uid of the user
     * @param   {String}    email       email address of the user
     * @param   {String}    name        name of the user
     * @param   {Boolean}   admin       is it an admin user?
     * @return  {Promise}   promise     promise object
     */

    static createUser(uid, email, name, admin) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Creating user

            firebase.database().ref(`/users/${uid}`).set({
                active:     true,
                admin:      admin,
                created:    firebase.database.ServerValue.TIMESTAMP,
                email:      email,
                name:       name
            }).then(() => {

                // User created successfully

                delete window.users;
                delete window.groups;
                resolve();
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });     
    }

    /**
     * Modifies the name and the admin rights of the user.
     * 
     * @param   {String}    uid         uid of the user
     * @param   {String}    name        name of the user
     * @param   {Boolean}   admin       is it an admin user?
     * @param   {Boolean}   active      is it an active user?
     * @return  {Promise}   promise     promise object
     */

    static modifyUser(uid, name, admin, active) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Modifying the user

            firebase.database().ref(`/users/${uid}`).update({
                active:     active,
                admin:      admin,
                name:       name
            }).then(() => {

                // User modified successfully

                delete window.users;
                delete window.groups;
                resolve();
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });     
    }

    /**
     * Stores the last login information for the specified user.
     * 
     * @param   {String}    uid         uid of the user
     * @return  {Promise}   promise     promise object
     */

    static refreshLastLogin(uid) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Storing the last login date

            firebase.database().ref(`/users/${uid}/lastlogin`).set(firebase.database.ServerValue.TIMESTAMP).then(() => {

                // Last login date saved successfully

                delete window.users;
                delete window.groups;
                resolve();
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });        
    }

    /**
     * Stores the settings of the specified user.
     * 
     * @param   {String}    uid         uid of the user
     * @param   {Object}    settings    settings of te user
     * @return  {Promise}   promise     promise object
     */

    static saveSettings(uid, settings) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Checking settings

            if (!settings.homedirectory) {
                delete settings.homedirectory;
            }

            // Storing the home directory

            firebase.database().ref(`/users/${uid}`).update(settings).then(() => {

                // Settings saved successfully

                delete window.users;
                delete window.groups;
                resolve();
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });        
    }

    /**
     * Loads all of the groups.
     * 
     * @return  {Promise}   promise     promise object
     */

    static loadGroup() {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Loading the groups

            firebase.database().ref('/groups').on('value', (snapshot) => {

                // Groups loaded successfully

                let result = snapshot.val();

                // Checking the result

                if (!result) {
                    resolve([]);
                }

                // Processing the result

                let groups = [];

                Object.keys(result).forEach((key) => {
                    let item = result[key];
                    item.id = key;
                    groups.push(item);
                });

                // Sorting the groups by name

                groups.sort((a, b) => {
                    let nameA = a.name.toUpperCase();
                    let nameB = b.name.toUpperCase();

                    if (nameA < nameB) {
                        return -1;
                    } else if (nameA > nameB) {
                        return 1;
                    } else {
                        return 0;
                    }                    
                });

                // Loading the users

                Service.loadUser().then((users) => {

                    // Getting the members of the groups

                    groups.forEach((group, index) => {
                        if (group.members) {
                            group.members.forEach((uid, p) => {
                                for (let i = 0; i < users.length; i++) {
                                    if (uid === users[i].uid) {
                                        groups[index].members[p] = users[i];
                                        break;
                                    }
                                }
                            });
                        } else {
                            groups[index].members = [];
                        }
                    });

                    // Resolving with the groups

                    window.groups = groups;
                    resolve(groups);
                }).catch((error) => {

                    // Error occured during the database reading

                    reject(error);
                });
            });
        });
    }

    /**
     * Saves the specified group.
     * 
     * @param   {Object}    group           group that has to save
     * @return  {Promise}   promise         promise object
     */

    static saveGroup(group) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Is it a group modification?

            if (group.id) {

                // Modify group

                let id = group.id;
                delete group.id;

                firebase.database().ref(`/groups/${id}`).set(group).then(() => {
    
                    // Group saved successfully
    
                    delete window.groups;
                    resolve();
                }).catch((error) => {
    
                    // Error occured during the database transaction
    
                    reject(error);
                });
            } else {
                
                // Creating group

                firebase.database().ref('/groups').push().set(group).then(() => {
    
                    // Group saved successfully
    
                    delete window.groups;
                    resolve();
                }).catch((error) => {
    
                    // Error occured during the database transaction
    
                    reject(error);
                });
            }
        });
    }

    /**
     * Deletes the specified group by ID.
     * 
     * @param   {String}    id              ID of the group
     * @return  {Promise}   promise         promise object
     */

    static deleteGroup(id) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Deleting the group

            firebase.database().ref(`/groups/${id}`).remove().then(() => {

                // Group deleted successfully

                delete window.groups;
                resolve();
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });
    }

    /**
     * Loads all of the attributes.
     * 
     * @return  {Promise}   promise     promise object
     */

    static loadAttribute() {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Loading the attributes

            firebase.database().ref('/attributes').on('value', (snapshot) => {

                // Attributes loaded successfully

                let result = snapshot.val() || [];

                // Processing attributes

                let attributes = [];

                Object.keys(result).forEach((key, index) => {
                    result[key].id = key;
                    attributes.push(result[key]);
                });

                // Sorting the attributes by name

                attributes.sort((a, b) => {
                    let nameA = a.name.toUpperCase();
                    let nameB = b.name.toUpperCase();

                    if (nameA < nameB) {
                        return -1;
                    } else if (nameA > nameB) {
                        return 1;
                    } else {
                        return 0;
                    }                    
                });

                // Resolving with the attributes

                window.attributes = attributes;
                resolve(attributes);
            });
        });
    }

    /**
     * Saves the specified attribute.
     * 
     * @param   {Object}    attribute       attribute that has to save
     * @return  {Promise}   promise         promise object
     */

    static saveAttribute(attribute) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Is it an attribute modification?

            if (attribute.id) {

                // Modify attribute

                let id = attribute.id;
                delete attribute.id;

                firebase.database().ref(`/attributes/${id}`).set(attribute).then(() => {
    
                    // Attribute saved successfully
    
                    delete window.attributes;
                    resolve();
                }).catch((error) => {
    
                    // Error occured during the database transaction
    
                    reject(error);
                });
            } else {
                
                // Creating attribute

                firebase.database().ref('/attributes').push().set(attribute).then(() => {
    
                    // Attribute saved successfully
    
                    delete window.attributes;
                    resolve();
                }).catch((error) => {
    
                    // Error occured during the database transaction
    
                    reject(error);
                });
            }
        });        
    }

    /**
     * Deletes the specified attribute by ID.
     * 
     * @param   {String}    id              ID of the attribute
     * @return  {Promise}   promise         promise object
     */

    static deleteAttribute(id) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Deleting the attribute

            firebase.database().ref(`/attributes/${id}`).remove().then(() => {

                // Attribute deleted successfully

                delete window.attributes;
                resolve();
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });
    }

    /**
     * Loads all of the entity types.
     * 
     * @return  {Promise}   promise     promise object
     */

    static loadEntitytype() {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Checking cache

            if (window.entitytypes) {
                resolve(JSON.parse(JSON.stringify(window.entitytypes)));
            } else {

                // Loading the entity types

                firebase.database().ref('/entitytypes').on('value', (snapshot) => {

                    // Entity types loaded successfully

                    let result = snapshot.val();

                    // Checking the result

                    if (!result) {
                        resolve([]);
                        return;
                    }

                    // Processing the result

                    let entityTypes = [];

                    Object.keys(result).forEach((key) => {
                        let item = result[key];
                        item.id = key;
                        entityTypes.push(item);
                    });

                    // Sorting the entity types by name

                    entityTypes.sort((a, b) => {
                        let nameA = a.name.toUpperCase();
                        let nameB = b.name.toUpperCase();

                        if (nameA < nameB) {
                            return -1;
                        } else if (nameA > nameB) {
                            return 1;
                        } else {
                            return 0;
                        }                    
                    });

                    // Loading the attributes

                    Service.loadAttribute().then((attributes) => {

                        // Loading the groups

                        Service.loadGroup().then((groups) => {

                            // Getting the attributes of the entity types

                            entityTypes.forEach((entityType, index) => {
                                if (entityType.attributes) {
                                    let attribs = [];
                                    let attribsObj = {};

                                    Object.keys(entityType.attributes).forEach((key) => {
                                        let item = entityType.attributes[key];
                                        item.id = key;

                                        for (let i = 0; i < attributes.length; i++) {
                                            if (item.id === attributes[i].id) {
                                                let attr = Object.assign({}, attributes[i], item);
                                                attr.ordernr = attr.ordernr === undefined ? i : attr.ordernr;
                                                attribs.push(attr);
                                                attribsObj[attr.id] = attr;
                                                break;
                                            }
                                        }
                                    });
                                    
                                    // Sorting the attributes by ordernr

                                    attribs.sort((a, b) => {
                                        if (a.ordernr < b.ordernr) {
                                            return -1;
                                        } else if (a.ordernr > b.ordernr) {
                                            return 1;
                                        } else {
                                            return 0;
                                        }                    
                                    });

                                    entityType.attributes = attribs;
                                    entityType.attributesObject = attribsObj;
                                }

                                // Getting the groups of the entity create access

                                if (entityType.entitycreateaccess) {
                                    entityType.entitycreateaccess.forEach((groupid, grindex) => {
                                        for (let i = 0; i < groups.length; i++) {
                                            if (groups[i].id === groupid) {
                                                entityType.entitycreateaccess[grindex] = groups[i];
                                                break;
                                            }
                                        }
                                    });
                                } else {
                                    entityType.entitycreateaccess = [];
                                }
                            });

                            // Resolving with the entity types

                            window.entitytypes = entityTypes;
                            resolve(entityTypes);
                        }).catch((error) => {

                            // Error occured during the database reading
        
                            reject(error);
                        });
                    }).catch((error) => {

                        // Error occured during the database reading

                        reject(error);
                    });
                });
            }
        });        
    }

    /**
     * Saves the specified entity type.
     * 
     * @param   {Object}    entitytype      entitytype that has to save
     * @return  {Promise}   promise         promise object
     */

    static saveEntitytype(entitytype) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Is it an entity type modification?

            if (entitytype.id) {

                // Modify entity type

                let id = entitytype.id;
                delete entitytype.id;

                firebase.database().ref(`/entitytypes/${id}`).set(entitytype).then(() => {
    
                    // Entity type saved successfully
    
                    delete window.users;
                    delete window.groups;
                    delete window.attributes;

                    // Resolving

                    resolve();
                }).catch((error) => {
    
                    // Error occured during the database transaction
    
                    reject(error);
                });
            } else {
                
                // Creating entity type

                firebase.database().ref('/entitytypes').push().set(entitytype).then(() => {
    
                    // Entity type saved successfully
    
                    delete window.users;
                    delete window.groups;
                    delete window.attributes;
                    resolve();
                }).catch((error) => {
    
                    // Error occured during the database transaction
    
                    reject(error);
                });
            }
        });
    }

    /**
     * Deletes the specified entity type by ID.
     * 
     * @param   {String}    id              ID of the entity type
     * @return  {Promise}   promise         promise object
     */

    static deleteEntitytype(id) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Deleting the entity type

            firebase.database().ref(`/entitytypes/${id}`).remove().then(() => {

                // Entity type deleted successfully

                delete window.users;
                delete window.groups;
                delete window.attributes;
                resolve();
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });
    }

    /**
     * Loads all of the folders.
     * 
     * @param   {String}    group       current group id
     * @return  {Promise}   promise     promise object
     */

    static loadFolder(group) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Checking cache

            if (window.folders) {
                resolve(JSON.parse(JSON.stringify(window.folders)));
            } else {

                // Loading the folders

                firebase.database().ref('/folders').on('value', (snapshot) => {

                    // Folders loaded successfully

                    let result = snapshot.val();
                    window.foldersobj = JSON.parse(JSON.stringify(result));

                    // Checking the result

                    if (!result) {
                        resolve([]);
                        return;
                    }

                    // Processing the result

                    let folders = [];

                    Object.keys(result).forEach((key) => {
                        let item = result[key];
                        
                        if (!group || !item.access || item.access[group]) {
                            item.id = key;
                            item.currentaccess = (!group || !item.access) ? 'rw' : item.access[group];
                            folders.push(item);
                        }
                    });

                    // Sorting the folders by name

                    folders.sort((a, b) => {
                        let nameA = a.name.toUpperCase();
                        let nameB = b.name.toUpperCase();

                        if (nameA < nameB) {
                            return -1;
                        } else if (nameA > nameB) {
                            return 1;
                        } else {
                            return 0;
                        }                    
                    });

                    // Processing the subfolders

                    let processSubfolder = (folder, parent, path, namedpath) => {
                        if (folder.folders) {
                            let flds = [];

                            Object.keys(folder.folders).forEach((key) => {
                                let item = folder.folders[key];
                                item.id = key;
                                item.path = path + '/folders/' + item.id;
                                item.namedpath = namedpath + '/' + item.name;
                                item.currentaccess = (!group || !item.access) ? 'rw' : item.access[group];

                                if (parent) {
                                    item.parent = parent;
                                }

                                flds.push(item);
                            });

                            // Sorting the folders by name

                            flds.sort((a, b) => {
                                let nameA = a.name.toUpperCase();
                                let nameB = b.name.toUpperCase();

                                if (nameA < nameB) {
                                    return -1;
                                } else if (nameA > nameB) {
                                    return 1;
                                } else {
                                    return 0;
                                }                    
                            });

                            flds.forEach((f) => {
                                let fld = processSubfolder(f, f.id, f.path, f.namedpath);

                                if (fld) {
                                    f.folders = fld;
                                }
                            });

                            return flds;
                        }

                        return undefined;
                    };

                    folders.forEach((folder) => {
                        folder.path = folder.id;
                        folder.namedpath = folder.name;
                        let fld = processSubfolder(folder, folder.id, folder.id, folder.name);

                        if (fld) {
                            folder.folders = fld;
                        }
                    });

                    // Resolving with the folders

                    window.folders = folders;
                    resolve(folders);
                });
            }
        });
    }

    /**
     * Creates a folder in the specified path and name.
     *
     * @param   {String}    path        path of the folder
     * @param   {String}    name        new name of the folder
     * @param   {Boolean}   root        create in the root folder
     * @param   {Object}    access      access rights of the folder
     * @return  {Promise}   promise     promise object
     */

    static createFolder(path, name, root, access) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {
            firebase.database().ref(root ? '/folders' : `/folders/${path}/folders`).push().set({ 
                name:   name,
                access: access
            }).then(() => {

                // Folder created successfully

                delete window.folders;
                resolve();
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });        
    }

    /**
     * Renames the specified folder.
     *
     * @param   {String}    path        path of the folder
     * @param   {String}    name        new name of the folder
     * @param   {Object}    access      access rights of the folder
     * @return  {Promise}   promise     promise object
     */

    static renameFolder(path, name, access) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {
            let update = {};
            update[`/folders/${path}/name`] = name;
            update[`/folders/${path}/access`] = access;

            firebase.database().ref().update(update).then(() => {

                // Folder renamed successfully

                delete window.folders;
                resolve();
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });        
    }

    /**
     * Deletes the specified folder by path.
     * 
     * @param   {String}    path            path of the folder
     * @return  {Promise}   promise         promise object
     */

    static deleteFolder(path) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Folder deletetion function

            let folderErase = () => {

                // Deleting the folder

                firebase.database().ref(`/folders/${path}`).remove().then(() => {

                    // Folder deleted successfully

                    delete window.folders;
                    resolve();
                }).catch((error) => {

                    // Error occured during the database transaction

                    reject(error);
                });
            };

            // Getting the entities of the folder

            firebase.database().ref(`/folders/${path}/entities`).once('value').then((snapshot) => {

                // Entity loaded successfully

                let result = snapshot.val();

                if (result) {
                    let keys = Object.keys(result);
                    let count = 0;

                    keys.forEach((key) => {
                        Service.deleteEntity(key).then(() => {
                            count++;

                            if (count === keys.length) {

                                // Delete the folder

                                folderErase();
                            }
                        }).catch((error) => {

                            // Error occured during the database transaction

                            reject(error);
                        });
                    });
                } else {

                    // Delete the folder

                    folderErase();
                }
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });
    }

    /**
     * Loads the specified entity by ID or all of the entities.
     * 
     * @param   {String}    id          ID of the entity (Optional)
     * @return  {Promise}   promise     promise object
     */

    static loadEntity(id) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {
            if (id) {

                // Loading the entity

                firebase.database().ref(`/entities/${id}`).once('value').then((snapshot) => {

                    // Entity loaded successfully

                    let result = snapshot.val();

                    // Checking the result

                    if (!result) {
                        resolve(null);
                    } else {
                        result.id = id;
                        resolve(result);
                    }
                }).catch((error) => {

                    // Error occured during the database reading

                    reject(error);
                });
            } else {

                // Loading the entities

                firebase.database().ref('/entities').once('value').then((snapshot) => {

                    // Entities loaded successfully

                    let result = snapshot.val();

                    // Checking the result

                    if (!result) {
                        resolve({});
                    } else {

                        // Resolving with the entities

                        resolve(result);
                    }
                }).catch((error) => {

                    // Error occured during the database reading

                    reject(error);
                });
            }
        });
    }

    /**
     * Saves the specified entity.
     * 
     * @param   {Object}    entity          entity that has to save
     * @return  {Promise}   promise         promise object
     */

    static saveEntity(entity) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Getting the primary value of the entity

            let primaryvalue = entity.primaryvalue;
            delete entity.primaryvalue;

            // Preparing

            let update = {};
            let id;

            // Is it an entity modification?

            if (entity.id) {

                // Modify entity

                id = entity.id;
                delete entity.id;

                update[`/folders/${entity.path}`] = primaryvalue;
            } else {
                
                // Creating entity ID

                id = firebase.database().ref('/entities').push().key;
                update[`/folders/${entity.path}/entities/${id}`] = primaryvalue;

                if (entity.path.indexOf('entities') < 0) {
                    entity.path = entity.path + '/entities/' + id;
                }
            }

            update[`/entities/${id}`] = entity;

            // Taking the transaction

            firebase.database().ref().update(update).then(() => {

                // Entity saved successfully

                resolve(id);
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });
    }

    /**
     * Deletes the specified entity by ID.
     * 
     * @param   {String}    id              ID of the entity
     * @param   {String}    path            path of the entity
     * @return  {Promise}   promise         promise object
     */

    static deleteEntity(id, path) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Preparing

            let remove = {};
            remove[`/entities/${id}`] = null;
            remove[`folders/${path}`] = null;

            // Deleting the entity

            firebase.database().ref().update(remove).then(() => {

                // Entity deleted successfully

                resolve();
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });
    }
    
    /**
     * Loads the dashboard.
     * 
     * @param   {String}    group       current group id
     * @return  {Promise}   promise     promise object
     */

    static loadDashboard(group) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Loading folders

            Service.loadFolder(group).then((folders) => {
                
                // Cloning

                folders = JSON.parse(JSON.stringify(folders));

                // Loading entities

                Service.loadEntity().then((entities) => {

                    // Adding entities to the folders

                    let addEntities = (folder) => {
                        if (folder.entities) {
                            let ents = [];

                            Object.keys(folder.entities).forEach((key) => {
                                if (entities[key]) {
                                    entities[key].id = key;
                                    entities[key].name = folder.entities[key];
                                    entities[key].path = folder.path + '/entities/' + key;
                                    entities[key].namedpath = folder.namedpath + '/' + entities[key].name;
                                    entities[key].access = folder.access;
                                    ents.push(entities[key]);
                                }
                            });

                            // Sorting the entities by name

                            ents.sort((a, b) => {
                                let nameA = ('' + a.name).toUpperCase();
                                let nameB = ('' + b.name).toUpperCase();

                                if (nameA < nameB) {
                                    return -1;
                                } else if (nameA > nameB) {
                                    return 1;
                                } else {
                                    return 0;
                                }                    
                            });

                            folder.entities = ents;
                        }

                        if (folder.folders) {
                            folder.folders.forEach((item) => {
                                addEntities(item);
                            });
                        }
                    };                   

                    folders.forEach((item) => {
                        addEntities(item);
                    });

                    // Resolving with the dashboard

                    resolve(folders);
                }).catch((error) => {        

                    // Error occured during the database reading

                    reject(error);
                });
            }).catch((error) => {

                // Error occured during the database reading

                reject(error);
            });
        });
    }

    /**
     * Searches entities based on the specified criteria.
     * 
     * @param   {Object}    criteria    search criteria
     * @return  {Promise}   promise     promise object
     */

    static search(criteria) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Getting the value based on the case insesitive search.

            let getValue = (value) => {
                return criteria.caseinsesitive ? value.toLowerCase() : value;
            };

            // Getting the folders

            let folders = window.foldersobj;
            folders = !folders ? {} : folders;

            // Getting all of the users

            Service.loadUser().then((users) => {

                // Getting all of the entities

                Service.loadEntity().then((entities) => {

                    // Preparing

                    let result = [];
                    let search = criteria.caseinsesitive ? criteria.search.toLowerCase() : criteria.search;

                    // Searching

                    Object.keys(entities).forEach((id) => {
                        let found = false;
                        let entity = entities[id];
                        entity.id = id;
                        let entitytype = criteria.entitytypes[entity.entitytype];

                        // Checking access rights

                        let fld = folders;

                        if (entity.path) {
                            let path = entity.path.split('/');

                            path.forEach((item) => {
                                fld = fld[item];
                            });
                        }

                        // Does the entity type specified?

                        if (entitytype && (!criteria.group || (criteria.group && fld.access))) {
                        
                            // Searching in tags

                            if (criteria.tags && entity.tags) {
                                for (let i = 0; i < entity.tags.length; i++) {
                                    if (search === '' || (criteria.pattern && getValue(entity.tags[i]).indexOf(search) >= 0) || 
                                    (!criteria.pattern && getValue(entity.tags[i]) === search)) {
                                        found = true;
                                        break;
                                    }
                                }
                            }

                            // Searching in attributes

                            if (!found) {

                                // Getting all of the versions

                                let versions = [];

                                Object.keys(entity.versions).forEach((version) => {
                                    versions.push(version.substring(1));
                                });

                                // Sorting the versions

                                if (!criteria.versions) {
                                    versions.sort((a, b) => {
                                        return parseInt(a, 10) - parseInt(b, 10);
                                    });

                                    versions = [versions[versions.length - 1]];
                                }

                                // Getting the attributes

                                for (let v = 0; v < versions.length; v++) {
                                    let attributes = entity.versions['v' + versions[v]].attributes;
                                    let attrids = Object.keys(attributes);

                                    if (!criteria.user || (criteria.user && entity.versions['v' + versions[v]].modifiedby === criteria.user.uid)) {
                                        if (!criteria.date || (criteria.date && entity.versions['v' + versions[v]].modificationdate >= criteria.date.getTime())) {
                                            for (let i = 0; i < attrids.length; i++) {                                           
                                                if ( entitytype.attributesObject[attrids[i]]) {
                                                    let type = entitytype.attributesObject[attrids[i]].type;
                                                    let multivalue = entitytype.attributesObject[attrids[i]].multivalue;
                                                    let attr = attributes[attrids[i]];

                                                    if (!entitytype.selectedattr || entitytype.selectedattr[attrids[i]]) {
                                                        
                                                        if (search === '') {
                                                            found = true;
                                                            break;
                                                        } else if (['text', 'multilinetext', 'externallink', 'radio'].indexOf(type) >= 0) {
                                                            if (multivalue) {
                                                                for (let a = 0; a < attr.length; a++) {
                                                                    if ((criteria.pattern && getValue(attr[a]).indexOf(search) >= 0) || 
                                                                    (!criteria.pattern && getValue(attr[a]) === search)) {
                                                                        found = true;
                                                                        break;
                                                                    }

                                                                    if (found) {
                                                                        break;
                                                                    }
                                                                }
                                                            } else {
                                                                if ((criteria.pattern && getValue(attr).indexOf(search) >= 0) || 
                                                                (!criteria.pattern && getValue(attr) === search)) {
                                                                    found = true;
                                                                    break;
                                                                }
                                                            }
                                                        } else if (['integer'].indexOf(type) >= 0) {
                                                            if (multivalue) {
                                                                for (let a = 0; a < attr.length; a++) {
                                                                    if (attr[a] === parseInt(search, 10)) {
                                                                        found = true;
                                                                        break;
                                                                    }
                                                                }

                                                                if (found) {
                                                                    break;
                                                                }
                                                            } else {
                                                                if (attr === parseInt(search, 10)) {
                                                                    found = true;
                                                                    break;
                                                                }
                                                            }
                                                        } else if (['float'].indexOf(type) >= 0) {
                                                            if (multivalue) {
                                                                for (let a = 0; a < attr.length; a++) {
                                                                    if (attr[a] === parseFloat(search)) {
                                                                        found = true;
                                                                        break;
                                                                    }
                                                                }

                                                                if (found) {
                                                                    break;
                                                                }
                                                            } else {
                                                                if (attr === parseFloat(search)) {
                                                                    found = true;
                                                                    break;
                                                                }
                                                            }
                                                        } else if (['date'].indexOf(type) >= 0 && attr !== '') {
                                                            if (multivalue) {
                                                                for (let a = 0; a < attr.length; a++) {
                                                                    if (new Date(attr[a]).toString().indexOf(search) >= 0) {
                                                                        found = true;
                                                                        break;
                                                                    }
                                                                }

                                                                if (found) {
                                                                    break;
                                                                }
                                                            } else {
                                                                if (new Date(attr).toString().indexOf(search) >= 0) {
                                                                    found = true;
                                                                    break;
                                                                }
                                                            }
                                                        } else if (['checkbox'].indexOf(type) >= 0 && attr) {
                                                            for (let j = 0; j < attr.length; j++) {
                                                                if ((criteria.pattern && getValue(attr[j]).indexOf(search) >= 0) || 
                                                                (!criteria.pattern && getValue(attr[j]) === search)) {
                                                                    found = true;
                                                                    break;    
                                                                }
                                                            }

                                                            if (found) {
                                                                break;
                                                            }
                                                        } else if (type === 'user') {
                                                            for (let i = 0; i < users.length; i++) {
                                                                if (users[i].uid === attr) {
                                                                    if ((criteria.pattern && getValue(users[i].email).indexOf(search) >= 0) || 
                                                                    (!criteria.pattern && getValue(users[i].email) === search)) {
                                                                        found = true;
                                                                    }
        
                                                                    break;
                                                                }
                                                            }

                                                            if (found) {
                                                                break;
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        // Adding the entity to the result if match

                        if (found) {
                            result.push(entity);
                        }
                    });

                    resolve(result.length === 0 ? null : result);
                }).catch((error) => {

                    // Error occured during the database reading

                    reject(error);
                });
            }).catch((error) => {

                // Error occured during the database reading

                reject(error);
            });
        });
    }

    /**
     * Loads all of the import configurations.
     * 
     * @return  {Promise}   promise     promise object
     */

    static loadImportConfiguration() {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Loading the import configurations

            firebase.database().ref('/importconfigurations').once('value').then((snapshot) => {

                // Import configurations loaded successfully

                let result = snapshot.val() || [];

                // Processing import configurations

                let configurations = [];

                Object.keys(result).forEach((key) => {
                    result[key].id = key;
                    configurations.push(result[key]);
                });

                // Loading entity types

                Service.loadEntitytype().then((entitytypes) => {
                    configurations.forEach((item) => {
                        for (let i = 0; i < entitytypes.length; i++) {
                            if (entitytypes[i].id === item.entitytype) {
                                item.entitytype = entitytypes[i];

                                item.attributes.forEach((it, index) => {
                                    if (it.id) {
                                        for (let j = 0; j < entitytypes[i].attributes.length; j++) {
                                            if (entitytypes[i].attributes[j].id === it.id) {
                                                let column = it.column;
                                                let primarykey = it.primarykey;
                                                let relation = it.relation;
                                                item.attributes[index] = JSON.parse(JSON.stringify(entitytypes[i].attributes[j]));
                                                item.attributes[index].column = column;
                                                item.attributes[index].primarykey = (primarykey === true);

                                                if (relation) {
                                                    item.attributes[index].relation = relation;
                                                }
                                                
                                                break;
                                            }
                                        }
                                    }
                                });

                                break;
                            }
                        }
                    });

                    // Sorting the import configurations by name

                    configurations.sort((a, b) => {
                        let nameA = a.name.toUpperCase();
                        let nameB = b.name.toUpperCase();

                        if (nameA < nameB) {
                            return -1;
                        } else if (nameA > nameB) {
                            return 1;
                        } else {
                            return 0;
                        }                    
                    });

                    // Resolving with the import configurations

                    resolve(configurations);
                });
            });
        });
    }

    /**
     * Saves the specified import configuration.
     * 
     * @param   {Object}    configuration   import configuration that has to save
     * @return  {Promise}   promise         promise object
     */

    static saveImportConfiguration(configuration) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Is it an import configuration modification?

            if (configuration.id) {

                // Modify import configuration

                let id = configuration.id;
                delete configuration.id;

                firebase.database().ref(`/importconfigurations/${id}`).set(configuration).then(() => {
    
                    // Resolving

                    resolve();
                }).catch((error) => {
    
                    // Error occured during the database transaction
    
                    reject(error);
                });
            } else {
                
                // Creating import configuration

                firebase.database().ref('/importconfigurations').push().set(configuration).then(() => {
    
                    // Resolving

                    resolve();
                }).catch((error) => {
    
                    // Error occured during the database transaction
    
                    reject(error);
                });
            }
        });
    }
 
    /**
     * Deletes the specified import configuration by ID.
     * 
     * @param   {String}    id              import configuration ID
     * @return  {Promise}   promise         promise object
     */

    static deleteImportConfiguration(id) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Deleting the import configuration

            firebase.database().ref(`/importconfigurations/${id}`).remove().then(() => {

                // Import configuration deleted successfully

                resolve();
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });
    }

    /**
     * Clears all entities that has the specified entity type.
     * 
     * @param   {String}    entitytype      ID of the entitytype
     * @return  {Promise}   promise         promise object
     */

    static clearAllEntities(entitytype) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Loading entities

            firebase.database().ref('/entities').once('value').then((snapshot) => {

                // Entities loaded successfully

                let result = snapshot.val() || {};
                let toRemove = {};
    
                Object.keys(result).forEach((key) => {
                    if (result[key].entitytype === entitytype) {
                        toRemove[`/entities/${key}`] = null;
                    }
                });

                firebase.database().ref().update(toRemove).then(() => {

                    // Resolving

                    resolve();
                }).catch((error) => {
                    
                    // Error occured during the database transaction

                    reject(error);
                });
            }).catch((error) => {
    
                // Error occured during the database transaction

                reject(error);
            });
        });        
    }

    /**
     * Loads the first entity specified by the primary attribute ID and key.
     * 
     * @param   {String}    entitytype      ID of the entitytype
     * @param   {String}    attribute       ID of the attribute
     * @param   {String}    primarykey      primary key
     * @param   {Boolean}   allentities     all entities must be returned
     * @return  {Promise}   promise         promise object
     */

    static loadEntityByPrimaryKey(entitytype, attribute, primarykey, allentities) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Loading entities

            firebase.database().ref('/entities').once('value').then((snapshot) => {

                // Entities loaded successfully

                let result = snapshot.val() || {};
                let keys = Object.keys(result);
                let found = false;
                let results = [];

                for (let i = 0; i < keys.length; i++) {
                    if (entitytype === result[keys[i]].entitytype) {
                        let version = Object.keys(result[keys[i]].versions);

                        version.sort((a, b) => {
                            let vA = parseInt(a.substring(1), 10);
                            let vB = parseInt(a.substring(1), 10);

                            if (vA < vB) {
                                return -1;
                            } else if (vA > vB) {
                                return 1;
                            } else {
                                return 0;
                            }                    
                        });

                        if (result[keys[i]].versions[version[version.length - 1]].attributes[attribute] === primarykey) {
                            found = true;

                            if (allentities) {
                                results.push({ entity: result[keys[i]] });
                            } else {
                                result[keys[i]].id = keys[i];
                                resolve({ entity: result[keys[i]], version: version[version.length - 1] });
                                break;
                            }
                        }
                    }
                }

                // Not found

                if (!found) {
                    resolve();
                } else {
                    if (allentities) {
                        resolve(results);
                    }
                }
            }).catch((error) => {
    
                // Error occured during the database transaction

                reject(error);
            });
        });
    }

    /**
     * Gets the named path for the specified path.
     * 
     * @param   {String}    path            path of the entity
     * @return  {Promise}   promise         promise object
     */

    static getNamedPath(path) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {
            let namedPath = '';
            let realPath = '/folders';
            let p = path.split('/');

            let getPath = (index, callback) => {
                if (index >= p.length) {
                    callback();
                } else {
                    if (['folders', 'entities'].indexOf(p[index]) >= 0) {
                        realPath += `/${p[index]}`;
                        getPath(index + 1, callback);
                    } else {
                        let attr = realPath.endsWith('folders') ? '/name' : '';
                        realPath += `/${p[index]}`;

                        firebase.database().ref(`${realPath}${attr}`).once('value').then((snapshot) => {
                            namedPath += `/${snapshot.val()}`;
                            getPath(index + 1, callback);
                        }).catch((error) => {
            
                            // Error occured during the database transaction
            
                            reject(error);
                        });
                    }
                }
            };

            getPath(0, () => {
                resolve({ path: path, namedpath: namedPath.substring(1) });
            });
        });       
    }

    /**
     * Moves the entity specified by ID to the specified path.
     * 
     * @param   {String}    id              ID of the entity
     * @param   {String}    name            name of the entity
     * @param   {String}    currentpath     current path of the entity
     * @param   {String}    newpath         new path of the entity
     * @return  {Promise}   promise         promise object
     */

    static moveEntity(id, name, currentpath, newpath) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Preparing

            let update = {};
            update[`/entities/${id}/path`] = newpath + '/entities/' + id;
            update[`/folders/${currentpath}`] = null;
            update[`/folders/${newpath}/entities/${id}`] = name;

            // Saving the path of the entity

            firebase.database().ref().update(update).then(() => {
    
                // Resolving

                resolve();
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });        
    }

    /**
     * Moves the folder specified by ID to the specified path.
     * 
     * @param   {String}    currentpath     current path of the entity
     * @param   {String}    newpath         new path of the entity
     * @param   {Array}     dashboard       dashboard content
     * @return  {Promise}   promise         promise object
     */

    static moveFolder(currentpath, newpath, dashboard) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Preparing

            let update = {};

            let path = currentpath.split('/');

            while (true) {
                let index = path.findIndex((item) => { return item === 'folders';});

                if (index >= 0) {
                    path.splice(index, 1);
                } else {
                    break;
                }
            }

            let getFolder = (folders, pos) => {
                let index = folders.findIndex((item) => { return item.id === path[pos]; });

                if (pos >= (path.length - 1)) {
                    return folders[index];
                } else {
                    if (index >= 0) {
                        return getFolder(folders[index].folders, pos + 1);
                    } else {
                        return null;
                    }
                }
            };

            let folder = getFolder(dashboard, 0);

            folder.entities.forEach((item) => {
                update[`/entities/${item.id}/path`] = newpath + '/entities/' + item.id;
            });

            update[`/folders/${currentpath}`] = null;

            firebase.database().ref(`/folders/${currentpath}`).once('value').then((snapshot) => {
                update[`/folders/${newpath}/folders/${folder.id}`] = snapshot.val();

                // Taking the transaction

                firebase.database().ref().update(update).then(() => {
        
                    // Resolving

                    resolve();
                }).catch((error) => {

                    // Error occured during the database transaction

                    reject(error);
                });
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });        
    }

    /**
     * Loads the business report.
     */

    static loadBusinessReport() {

        // Constanses

        const ENTITYTYPE_NAME = 'Afacere';
        const ATTRIBUTE_NAME = 'Stadiu';
        const SUM_ATTRIBUTE_NAME = 'Sumă_EUR';

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Getting the "Stadiu" attribute

            this.loadAttribute().then((attribute) => {
                let attrID = attribute.find((attr) => { return attr.name === ATTRIBUTE_NAME; });

                if (!attrID) {
                    throw new Error('Missing "Stadiu" attribute');
                } else {
                    attrID = attrID.id;

                    let sumAttrID = attribute.find((attr) => { return attr.name === SUM_ATTRIBUTE_NAME; });

                    if (!sumAttrID) {
                        throw new Error('Missing "Sumă_EUR" attribute');
                    } else {
                        sumAttrID = sumAttrID.id;
    
                        // Getting the "Afacere" entity type

                        this.loadEntitytype().then((entitytype) => {
                            let et = entitytype.find((et) => { return et.name === ENTITYTYPE_NAME; });

                            if (!et) {
                                throw new Error('Missing "Afacere" entity type');
                            } else {

                                let entityTypeID = et.id;

                                firebase.database().ref('/entities').once('value').then((snapshot) => {
                                    let entities = snapshot.val();

                                    if (entities) {
                                        let afacereEntityIDs = Object.keys(entities).filter( entityID => entities[entityID].entitytype === entityTypeID);
                                        let afacereEntities = [];

                                        afacereEntityIDs.forEach((id) => {
                                            afacereEntities.push(entities[id]);
                                        });

                                        let groups = [];

                                        afacereEntities.forEach((entry) => {
                                            let versions = Object.keys(entry.versions);

                                            // Sorting the versions
                                    
                                            versions.sort((a, b) => {
                                                return parseInt(a.substring(1), 10) - parseInt(b.substring(1), 10);
                                            });
                                    
                                            let attributes = entry.versions[versions[versions.length - 1]].attributes;
                                            let attrValue = attributes[attrID] || '-';
                                            let index = groups.findIndex((group) => { return group.id === attrValue; });

                                            if (index >= 0) {
                                                groups[index].entities.count++;
                                                groups[index].entities.sum+=(attributes[sumAttrID] || 0);
                                            } else {
                                                groups.push({ id: attrValue, entities: { count: 1, sum: attributes[sumAttrID] || 0 } });
                                            }
                                        });
                                        
                                        // Resolving
                            
                                        resolve(groups);        
                                    } else {

                                        // Empty result
                                        resolve([]);
                                    }
                                }).catch((error) => {
                        
                                    // Error occured during the database transaction
                    
                                    reject(error);
                                });        
                            }
                        }).catch((error) => {

                            // Error occured during the database transaction

                            reject(error);
                        });
                    }
                }
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });        
    }

    /**
     * Saves the specified comment to the entity.
     * 
     * @param   {String}    id              ID of the entity
     * @param   {Object}    comment         comment that has to save
     * @return  {Promise}   promise         promise object
     */

    static saveComment(id, comment) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {
        
            // Saving

            let path = `/entities/${id}/comments`;
            let commentID = firebase.database().ref(path).push().key;
            
            firebase.database().ref(`${path}/${commentID}`).set(comment).then(() => {
            
                // Resolving

                resolve(commentID);
            }).catch((error) => {

                // Error occured during the database transaction

                reject(error);
            });
        });
    }    
}


// WEBPACK FOOTER //
// ./src/components/Util/Service.js